/**
 * MODIFIED FROM SHADCN ORIGINAL
 *
 * This component has been customized from the original shadcn/ui version.
 * Modifications: Added CardAction component; Custom container query support
 *
 * Original: https://ui.shadcn.com/
 */

import { cn } from '@ophelos/ui/lib/utils';
import * as React from 'react';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Controls where the CardHeader is positioned relative to the card box.
   * - "inside" (default): Header appears inside the card's border/background
   * - "outside": Header appears above the card, outside the border/background
   */
  headerPosition?: 'inside' | 'outside';
  /** "fullscreen" bleeds the card to the screen edge below the xs breakpoint, cancelling the page's px-4 gutter. */
  variant?: 'default' | 'fullscreen';
}

const FULLSCREEN = 'max-xs:-mx-4 max-xs:rounded-none! max-xs:border-x-0';

const CardContext = React.createContext<{ headerPosition: 'inside' | 'outside' }>({ headerPosition: 'inside' });

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, headerPosition = 'inside', variant = 'default', children, ...props }, ref) => {
    if (headerPosition === 'outside') {
      // Extract CardHeader from children to render it outside the card box
      const childArray = React.Children.toArray(children);
      const isCardHeader = (child: React.ReactNode): boolean => {
        if (!React.isValidElement(child)) return false;
        const type = child.type as { displayName?: string };
        return type.displayName === 'CardHeader';
      };
      const headerChild = childArray.find(isCardHeader);
      const otherChildren = childArray.filter((child) => !isCardHeader(child));

      return (
        <CardContext.Provider value={{ headerPosition }}>
          <div ref={ref} data-slot="card-wrapper" className={cn('flex flex-col gap-2', className)} {...props}>
            {headerChild}
            <div
              data-slot="card"
              className={cn(
                'bg-card text-card-foreground flex flex-col rounded-xl border gap-3 py-4',
                variant === 'fullscreen' && FULLSCREEN
              )}
            >
              {otherChildren}
            </div>
          </div>
        </CardContext.Provider>
      );
    }

    return (
      <CardContext.Provider value={{ headerPosition }}>
        <div
          ref={ref}
          data-slot="card"
          className={cn(
            'bg-card text-card-foreground flex flex-col rounded-xl border py-5 gap-3',
            variant === 'fullscreen' && FULLSCREEN,
            className
          )}
          {...props}
        >
          {children}
        </div>
      </CardContext.Provider>
    );
  }
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
  const { headerPosition } = React.useContext(CardContext);
  return (
    <div
      ref={ref}
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start has-data-[slot=card-action]:gap-x-2 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        headerPosition === 'inside' && 'px-5',
        className
      )}
      {...props}
    />
  );
});
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="card-title" className={cn('font-semibold', className)} {...props} />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="card-description" className={cn('text-muted-foreground text-sm', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

const CardAction = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-action"
    className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
    {...props}
  />
));
CardAction.displayName = 'CardAction';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="card-content" className={cn('px-5', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="card-footer" className={cn('flex items-center px-6 [.border-t]:pt-6', className)} {...props} />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardAction, CardContent, CardContext, CardDescription, CardFooter, CardHeader, type CardProps, CardTitle };
